import { DEDUPING_INTERVAL } from "@/constants";
import { useHandleError } from "@/hooks/useHandleError";
import { getAuthenticatedUser } from "@/store/auth.store";
import LoadingSpinner from "@roman/shared/components/LoadingSpinner";
import { useRoundEntryUnit } from "@roman/shared/hooks/soccer/useRoundEntryUnit";
import { useRoundEntryUnitResult } from "@roman/shared/hooks/soccer/useRoundEntryUnitResult";
import { useSoccerRound } from "@roman/shared/hooks/soccer/useSoccerRound";
import { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { SelectPlayers } from "../Rounds/SelectPlayers";

export const ResultSelectedPlayerPage: React.FC = () => {
  const { roundEntryUnitId, entryId } = useParams<{ roundEntryUnitId: string; entryId: string }>();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location]);
  const playerId = queryParams.get("playerId") ?? "";
  const [currentIndex, setCurrentIndex] = useState(0);

  const user = getAuthenticatedUser();

  if (!roundEntryUnitId) {
    throw new Error("Invalid roundEntryUnitId in SelectPlayerPage");
  }

  const { data: roundEntryUnitResultData, isLoading: isRoundEntryUnitResultLoading } = useRoundEntryUnitResult(
    user.id,
    roundEntryUnitId,
    "ja",
    DEDUPING_INTERVAL,
  );
  const { roundEntryUnit: roundEntryUnitData, isLoading: isRoundEntryUnitDataLoading } = useRoundEntryUnit({
    roundEntryUnitId,
    lang: "ja",
    swrOptions: {
      dedupingInterval: DEDUPING_INTERVAL,
      onError: useHandleError("ラウンドエントリーユニットのデータの取得に失敗しました"),
    },
  });

  const { data: roundData, isLoading: isRoundDataLoading } = useSoccerRound({
    roundId: roundEntryUnitData?.roundId || "",
    lang: "ja",
    swrOptions: {
      dedupingInterval: DEDUPING_INTERVAL,
    },
  });

  const round = roundData?.round;
  const entries = roundEntryUnitResultData?.result.entries || [];
  const maxPlayerSelections = round?.type === "Round0001" ? 11 : 5;

  const { homePlayers = [], awayPlayers = [] } = round?.players || {};

  const selectedPlayers = useMemo(() => {
    const players: string[] = [];

    if (!entryId) return players;

    const selectedEntry = entries.find((entry) => entry.id === entryId);
    if (!selectedEntry) return players;

    const selectedPlayersMap = new Map(selectedEntry.selectedPlayers.map((id) => [id, true]));

    for (let i = 0; i < maxPlayerSelections; i++) {
      const homePlayer = homePlayers[i];
      const awayPlayer = awayPlayers[i];

      const selectedPlayer = selectedPlayersMap.get(homePlayer?.id) ? homePlayer?.id : awayPlayer?.id;

      if (selectedPlayer) {
        players.push(selectedPlayer);
      }

      if (homePlayer?.id === playerId || awayPlayer?.id === playerId) {
        setCurrentIndex(i);
      }
    }

    return players;
  }, [entryId, entries, homePlayers, awayPlayers, maxPlayerSelections, playerId]);

  const isLoading = isRoundEntryUnitDataLoading || isRoundDataLoading || isRoundEntryUnitResultLoading;

  if (!round || isLoading) {
    return (
      <div className="w-full h-full text-center content-center bg-light-blue">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <SelectPlayers
        round={round}
        setPlayerRowIndex={setCurrentIndex}
        selectedPlayers={selectedPlayers}
        isSubmitButtonEnable={false}
        currentIndex={currentIndex}
      />
    </div>
  );
};
